/**
 * Stale-while-revalidate cache for the dashboard cards.
 *
 * The dashboard previously hit the network on every mount/pull-to-refresh and
 * showed a spinner until each request resolved. The "daily goals" barely change
 * during the day, so we now:
 *   1. apply the last-known value from `@capacitor/preferences` immediately
 *      (instant render, also works offline), then
 *   2. revalidate against the network only when the cache is "stale".
 *
 * A cached card is considered stale when ANY of these holds:
 *   - it is older than {@link REVALIDATE_TTL_MS} (default 1 hour), or
 *   - it was stored on a different calendar day (so the first open of a new day
 *     always shows fresh daily goals), or
 *   - a pull-to-refresh happened since it was stored (see {@link forceDashboardRefresh}).
 *
 * Otherwise the network call is skipped entirely — repeated navigations within
 * the hour just re-use the cache.
 *
 * The read-path here is intentionally compatible with the planned
 * `OfflineCacheService` (see `docs/OFFLINE_MODE_PLAN.md`); it can be folded into
 * it later without changing the call sites.
 */
import { Preferences } from "@capacitor/preferences";
import type { Ref } from "vue";

const PREFIX = "dashboard_cache_v1_";

/** How long a cached card stays "fresh" before we revalidate in the background. */
export const REVALIDATE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Epoch (ms) before which every cache entry counts as stale. Bumped by
 * {@link forceDashboardRefresh} on pull-to-refresh; module-level so it survives
 * the card remount that pull-to-refresh triggers.
 */
let forceRevalidateAfter = 0;

/** Every cached dashboard card. Drives {@link clearDashboardCache}. */
export const DASHBOARD_CACHE_KEYS = [
  "plan",
  "goal",
  "habit",
  "progress",
  "checkins",
  "announcement",
  "sharedNotes",
] as const;

export type DashboardCacheKey = (typeof DASHBOARD_CACHE_KEYS)[number];

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface SwrOptions {
  /** Flipped to false as soon as we have *something* to show (cache or fresh). */
  isLoading?: Ref<boolean>;
}

/** Local-calendar-day key, so a new day invalidates yesterday's cache. */
function localDayKey(timestamp: number): string {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Force the next dashboard load to revalidate every card, ignoring the TTL.
 * Called by pull-to-refresh. Cheap + synchronous so it can run right before the
 * cards remount.
 */
export function forceDashboardRefresh(): void {
  forceRevalidateAfter = Date.now();
}

/**
 * Show cached data instantly, then revalidate from the network when stale.
 *
 * @param key     Dashboard card identifier (namespaced + persisted).
 * @param fetcher Network call returning the fresh value.
 * @param apply   Writes a value into the component's reactive state. Called with
 *                the cached value (if any) and, when revalidated, the fresh value.
 *                Keep any "is this response usable?" guards inside `apply`.
 * @throws Re-throws the fetch error only when there was no cache to fall back on,
 *         so the caller's existing try/catch can log the cold-start failure.
 */
export async function swrDashboard<T>(
  key: DashboardCacheKey,
  fetcher: () => Promise<T>,
  apply: (data: T) => void,
  options: SwrOptions = {}
): Promise<void> {
  const cacheKey = PREFIX + key;
  const { isLoading } = options;
  let entry: CacheEntry<T> | null = null;

  // 1. Instant render from the last-known value.
  try {
    const cached = await Preferences.get({ key: cacheKey });
    if (cached.value) {
      const parsed = JSON.parse(cached.value);
      // Ignore legacy/corrupt shapes — treat as no cache, fall through to fetch.
      if (parsed && typeof parsed.timestamp === "number") {
        entry = parsed as CacheEntry<T>;
        apply(entry.data);
        if (isLoading) isLoading.value = false;
      }
    }
  } catch (error) {
    console.error(`[dashboardCache] failed to read cache for "${key}"`, error);
    entry = null;
  }

  // 2. Skip the network when the cache is still fresh.
  const now = Date.now();
  const stale =
    !entry ||
    now - entry.timestamp > REVALIDATE_TTL_MS ||
    localDayKey(entry.timestamp) !== localDayKey(now) ||
    entry.timestamp <= forceRevalidateAfter;

  if (!stale) {
    if (isLoading) isLoading.value = false;
    return;
  }

  // 3. Revalidate in the background and persist the fresh result.
  try {
    const fresh = await fetcher();
    apply(fresh);
    await Preferences.set({
      key: cacheKey,
      value: JSON.stringify({ data: fresh, timestamp: Date.now() } as CacheEntry<T>),
    });
  } catch (error) {
    if (!entry) throw error; // cold start with no fallback — let caller handle
    // Otherwise keep showing the cached data (e.g. offline / transient 500).
    console.error(`[dashboardCache] revalidate failed for "${key}"`, error);
  } finally {
    if (isLoading) isLoading.value = false;
  }
}

/**
 * Drop one card's cache, so its next read goes to the network.
 *
 * For a card whose content the user CHANGES from the app (the announcement card
 * dismisses a row): the cached list still holds what was just removed, and the
 * TTL would serve that back for another hour.
 */
export async function clearDashboardCacheKey(key: DashboardCacheKey): Promise<void> {
  await Preferences.remove({ key: PREFIX + key }).catch(() => {});
}

/** Purge all dashboard caches. Call on logout so data can't leak between accounts. */
export async function clearDashboardCache(): Promise<void> {
  await Promise.all(DASHBOARD_CACHE_KEYS.map((key) => clearDashboardCacheKey(key)));
}
